import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { CoverageRow, InvoiceCoverage, InvoiceDetail } from "../../api/types";
import { calendarDayText, dateText, money } from "../../i18n/intl";
import { isBill } from "../../utils/paperUse";
import { ReasonModal } from "../ui/ReasonModal";

const rowKey = (r: CoverageRow) => `${r.kind}:${r.id}`;

function rowText(r: CoverageRow): string {
  const what = r.kind === "albaran" ? `Albarán ${r.label}` : `Delivery typed by hand, ${r.label}`;
  const when = r.date ? calendarDayText(r.date) : "no date";
  // OD-174: a hand-made delivery can be linked before it arrives; say so.
  const arrival = r.kind === "delivery" && !r.broughtStock ? " · not arrived yet" : "";
  return `${what} · ${when} · ${r.location.name}${arrival}`;
}

/**
 * G-131 — the factura ↔ albarán link, on the paper's own page.
 *
 * An albarán says which factura counts it, or that it is still waiting. A
 * factura lists what it covers (with Unlink) and the albaranes and hand-made
 * deliveries of its supplier still waiting (tick and Link). Every refusal and
 * sentence about scheduling comes from the server (`paper-rules.ts`); this
 * block only shows and sends.
 */
export function PaperCoverageBlock({
  invoice,
  onChanged,
}: {
  invoice: InvoiceDetail;
  /** The paper page reloads its preview: linking can open or close "Schedule delivery". */
  onChanged: () => void;
}) {
  const [coverage, setCoverage] = useState<InvoiceCoverage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmNoAlbaran, setConfirmNoAlbaran] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    api
      .getInvoiceCoverage(invoice.id)
      .then((c) => {
        if (!cancelled) setCoverage(c);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load the link");
      });
    return () => {
      cancelled = true;
    };
    // The paper's use and status decide what the server answers.
  }, [invoice.id, invoice.paperUse, invoice.status]);

  const candidates = useMemo(() => coverage?.candidates ?? [], [coverage]);
  const pickedRows = candidates.filter((r) => picked.has(rowKey(r)));

  const act = async (run: () => Promise<InvoiceCoverage>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const next = await run();
      setCoverage(next);
      setPicked(new Set());
      onChanged();
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save the link");
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="inv-coverage">
        <div className="alert error">{loadError}</div>
      </div>
    );
  }
  if (!coverage) return null;

  if (invoice.paperUse === "ALBARAN") {
    return (
      <div className="inv-coverage">
        <div className="inv-coverage-head">
          <strong>Factura</strong>
        </div>
        {coverage.coveredBy ? (
          <p className="inv-section-note">
            Counted by factura <Link to={`/app/invoices/${coverage.coveredBy.id}`}>{coverage.coveredBy.label}</Link>. Its
            IVA and what was paid count on the factura; this albarán&apos;s prices stay the stock cost.
          </p>
        ) : (
          <p className="inv-section-note">
            <span className="paper-pill paper-pill--warn">Waiting for a factura</span> This albarán counts no money
            until its factura is linked. Open the factura and link this albarán there:{" "}
            <Link to={`/app/invoices?supplier=${encodeURIComponent(invoice.supplierId)}`}>
              {invoice.supplier.name}&apos;s papers
            </Link>
            .
          </p>
        )}
      </div>
    );
  }

  if (!isBill(invoice.paperUse)) return null;


  const canDeclareNoAlbaran =
    invoice.paperUse === "FACTURA_WITH_GOODS" &&
    coverage.covers.length === 0 &&
    coverage.coversNoAlbaran === null &&
    (candidates.length > 0 || coverage.waitingElsewhere > 0);

  return (
    <div className="inv-coverage">
      <div className="inv-coverage-head">
        <strong>Albaranes and deliveries this factura bills</strong>
      </div>
      {error ? <div className="alert error">{error}</div> : null}

      {coverage.covers.length === 0 ? (
        <p className="inv-section-note muted">Not linked to any albarán or delivery yet.</p>
      ) : (
        <>
          <ul className="inv-coverage-list">
            {coverage.covers.map((r) => (
              <li key={rowKey(r)} className="inv-coverage-row">
                <Link to={r.kind === "albaran" ? `/app/invoices/${r.id}` : `/app/deliveries/${r.id}`}>{rowText(r)}</Link>
                <span className="inv-coverage-money">{r.totalEur !== null ? money(r.totalEur) : "no total"}</span>
                <button
                  type="button"
                  className="button plain"
                  disabled={busy}
                  onClick={() => void act(() => api.unlinkInvoiceCoverage(invoice.id, r.kind, r.id))}
                >
                  Unlink
                </button>
              </li>
            ))}
          </ul>
          <p className="inv-section-note muted">
            Together {money(coverage.coversTotal.eur)} before IVA
            {coverage.coversTotal.withoutTotal > 0 ? ` (${coverage.coversTotal.withoutTotal} without a total)` : ""}
            {invoice.baseEur !== null ? `; this factura prints ${money(invoice.baseEur)} before IVA.` : "."}
          </p>
        </>
      )}

      {coverage.coversNoAlbaran ? (
        <p className="inv-section-note">
          Said on {dateText(new Date(coverage.coversNoAlbaran.at))}: there is no albarán, the goods came with this
          factura.
        </p>
      ) : null}

      {candidates.length > 0 ? (
        <>
          <p className="inv-section-note">
            Not linked to a factura yet, from {invoice.supplier.name}, dated on or before this one. Tick the ones this
            factura bills:
          </p>
          <ul className="inv-coverage-list">
            {candidates.map((r) => {
              const key = rowKey(r);
              const id = `cover-${key.replace(":", "-")}`;
              return (
                <li key={key} className="inv-coverage-row">
                  <input
                    id={id}
                    type="checkbox"
                    checked={picked.has(key)}
                    disabled={busy}
                    onChange={(e) =>
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(key);
                        else next.delete(key);
                        return next;
                      })
                    }
                  />
                  <label htmlFor={id}>{rowText(r)}</label>
                  <span className="inv-coverage-money">{r.totalEur !== null ? money(r.totalEur) : "no total"}</span>
                </li>
              );
            })}
          </ul>
          <div className="button-row">
            <button
              type="button"
              className="button"
              disabled={busy || pickedRows.length === 0}
              onClick={() =>
                void act(() =>
                  api.linkInvoiceCoverage(invoice.id, {
                    albaranIds: pickedRows.filter((r) => r.kind === "albaran").map((r) => r.id),
                    deliveryIds: pickedRows.filter((r) => r.kind === "delivery").map((r) => r.id),
                  }),
                )
              }
            >
              {busy ? "Linking…" : pickedRows.length > 0 ? `Link ${pickedRows.length} to this factura` : "Link to this factura"}
            </button>
          </div>
        </>
      ) : coverage.covers.length === 0 ? (
        <p className="inv-section-note muted">Nothing from {invoice.supplier.name} is left without a factura.</p>
      ) : null}

      {coverage.waitingElsewhere > 0 ? (
        <p className="inv-section-note muted">
          {coverage.waitingElsewhere === 1
            ? "1 more waits in a shop you cannot see."
            : `${coverage.waitingElsewhere} more wait in shops you cannot see.`}
        </p>
      ) : null}

      {canDeclareNoAlbaran ? (
        <div className="button-row">
          <button type="button" className="button tinted" disabled={busy} onClick={() => setConfirmNoAlbaran(true)}>
            No albarán — the goods came with this factura
          </button>
        </div>
      ) : null}

      {confirmNoAlbaran ? (
        <ReasonModal
          title="The goods came with this factura?"
          message={`Say this only when no albarán of ${invoice.supplier.name} is for these goods. This factura can then become a delivery, and the papers waiting stay waiting for their own factura.`}
          reasonMode="none"
          confirmLabel="Yes, no albarán"
          busy={busy}
          error={error}
          onConfirm={async () => {
            if (await act(() => api.declareInvoiceCoversNoAlbaran(invoice.id))) setConfirmNoAlbaran(false);
          }}
          onClose={() => setConfirmNoAlbaran(false)}
        />
      ) : null}
    </div>
  );
}
